import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import type { SubProduct } from '@atlassian/jira-common-constants/src/sub-product-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useContainerContext } from '@atlassian/jira-providers-container-context/src/main.tsx';

/**
 * Maps a given project type to its corresponding sub-product. This function is designed to
 * facilitate the identification of product categories based on project characteristics.
 * Special cases are handled explicitly, with a fallback to 'unknown' for unlisted types,
 * ensuring broad applicability across different project contexts.
 */
export const subProductFromProjectType = (projectType: ProjectType): SubProduct => {
	switch (projectType) {
		case 'business':
			return 'core';
		case 'product_discovery': // for now, product discovery/polaris is bound to JSW
		case 'software':
			return 'software';
		case 'service_desk':
			return 'serviceDesk';
		case 'customer_service':
			if (fg('berry-6207-csm-analytic-subproduct')) {
				if (fg('csm-dropbears-analitycs-subproduct-change')) {
					return 'customer-service-management';
				}
				return 'customer-service';
			}
			return 'unknown';
		default:
			// prettier-ignore
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(projectType as never);
			return 'unknown';
	}
};

export const useSubProduct = () => {
	const [{ data }] = useContainerContext();
	// @ts-expect-error - TS2339 - Property 'project' does not exist on type 'ContainerContext'.
	if (!data?.project) return null;

	// @ts-expect-error - TS2339 - Property 'project' does not exist on type 'ContainerContext'.
	return subProductFromProjectType(data.project.type);
};

/**
 * A custom mapping function that translates project types to sub-products specifically for Analytics.
 * This function is tailored to ensure that the 'product_discovery' project type is consistently
 * mapped to the 'polaris' sub-product. In contrast, the original `subProductFromProjectType` is used in places
 * where the 'product_discovery' project type is associated with 'software'. (like billing related code)
 * This distinction is crucial for maintaining accurate analytics and reporting.
 */
export const subProductFromProjectTypeForAnalytics = (projectType: ProjectType): SubProduct => {
	if (projectType === 'product_discovery') {
		return 'polaris';
	}

	return subProductFromProjectType(projectType);
};

export const useSubProductForAnalytics = () => {
	const [{ data }] = useContainerContext();
	// @ts-expect-error - TS2339 - Property 'project' does not exist on type 'ContainerContext'.
	if (!data?.project) return null;

	// @ts-expect-error - TS2339 - Property 'project' does not exist on type 'ContainerContext'.
	return subProductFromProjectTypeForAnalytics(data.project.type);
};
